import { createSelector } from 'reselect';
import moment from 'moment';
import {
  SORT_FIELDS,
  CONVERSATION_STATUSES,
  CLOSE_REASONS,
  SCHEDULES,
  IS_GROUPED_APPOINTMENT,
  CONVERSATION_TYPES,
} from 'modules/queues/constants/appointmentsFilters';

const getActiveSortFieldSelector = state => state.queues.archive.archiveAppointmentsFilters.activeSortField;
const getLastConversationStatusesSelector = state => state.queues.archive.archiveAppointmentsFilters.lastConversationStatuses;
const getCloseReasonsSelector = state => state.queues.archive.archiveAppointmentsFilters.closeReasons;
const getDateRangeSelector = state => state.queues.archive.archiveAppointmentsFilters.dateRange;
const getScheduleTypesSelector = state => state.queues.archive.archiveAppointmentsFilters.scheduleTypes;
const getCheckedServicesSelector = state => state.queues.archive.archiveAppointmentsFilters.services;
const getIsGroupedSelector = state => state.queues.archive.archiveAppointmentsFilters.isGroupAppointment;
const getConversationTypesSelector = state => state.queues.archive.archiveAppointmentsFilters.conversationTypes;
const getTextSearchFilter = state => state.queues.archive.archiveAppointmentsFilters.textSearch;

const getServicesSelector = state => state.common.services.services;
const getIntlSelector = (_, ownProps) => ownProps.intl;

const getTranlsatedConversationStatuses = createSelector(
  getIntlSelector,
  intl => CONVERSATION_STATUSES.map(item => ({
    ...item,
    label: intl.formatMessage(item.label),
  })),
);

const getTranlsatedCloseReasonsStatuses = createSelector(
  getIntlSelector,
  intl => CLOSE_REASONS.map(item => ({
    ...item,
    label: intl.formatMessage(item.label),
  })),
);

const getTranslatedConversationTypes = createSelector(
  getIntlSelector,
  intl => CONVERSATION_TYPES.map(item => ({
    ...item,
    label: intl.formatMessage(item.label),
  })),
);

const getTranslatedSchedulesTypes = createSelector(
  getIntlSelector,
  intl => SCHEDULES.map(item => ({
    ...item,
    label: intl.formatMessage(item.label),
  })),
);

const getTranlsatedIsGroup = createSelector(
  getIntlSelector,
  intl => IS_GROUPED_APPOINTMENT.map(item => ({
    ...item,
    label: intl.formatMessage(item.label),
  })),
);

export const getLastConversationStatusesWithDescription = createSelector(
  getIntlSelector,
  getLastConversationStatusesSelector,
  getTranlsatedConversationStatuses,
  (intl, conversationStatuses, translatedStatuses) => {
    const statuses = translatedStatuses.map(status => ({
      ...status,
      checked: conversationStatuses.includes(status.key),
    }));

    const description = conversationStatuses.length === 0 || conversationStatuses.length === CONVERSATION_STATUSES.length
      ? intl.formatMessage({ id: 'General.All', defaultMessage: '** All' })
      : statuses.filter(({ checked }) => checked).map(({ label }) => label).join(', ');

    return {
      statuses,
      description,
    };
  },
);

export const getConversationTypesStatusesWithDescription = createSelector(
  getIntlSelector,
  getConversationTypesSelector,
  getTranslatedConversationTypes,
  (intl, conversationTypes, translatedConversationTypes) => {
    const statuses = translatedConversationTypes.map(status => ({
      ...status,
      checked: conversationTypes.includes(status.key),
    }));

    const description = conversationTypes.length === 0 || conversationTypes.length === CONVERSATION_TYPES.length
      ? intl.formatMessage({ id: 'General.All', defaultMessage: '** All' })
      : statuses.filter(({ checked }) => checked).map(({ label }) => label).join(', ');

    return {
      statuses,
      description,
    };
  },
);

export const getSchedulesTypesWithDescription = createSelector(
  getIntlSelector,
  getScheduleTypesSelector,
  getTranslatedSchedulesTypes,
  (intl, _scheduleTypes, translatedSchedulesTypes) => {
    const scheduleTypes = translatedSchedulesTypes.map(scheduleType => ({
      ...scheduleType,
      checked: _scheduleTypes.includes(scheduleType.key),
    }));

    const description = _scheduleTypes.length === 0 || _scheduleTypes.length === SCHEDULES.length
      ? intl.formatMessage({ id: 'General.All', defaultMessage: '** All' })
      : scheduleTypes.filter(({ checked }) => checked).map(({ label }) => label).join(', ');

    return {
      scheduleTypes,
      description,
    };
  },
);

export const getCloseReasonsWithDescription = createSelector(
  getIntlSelector,
  getCloseReasonsSelector,
  getTranlsatedCloseReasonsStatuses,
  (intl, closeReasons, translatedStatuses) => {
    const reasons = translatedStatuses.map(reason => ({
      ...reason,
      checked: closeReasons.includes(reason.key),
    }));

    const description = closeReasons.length === 0 || closeReasons.length === CLOSE_REASONS.length
      ? intl.formatMessage({ id: 'General.All', defaultMessage: '** All' })
      : reasons.filter(({ checked }) => checked).map(({ label }) => label).join(', ');

    return {
      reasons,
      description,
    };
  },
);

export const getIsGroupedStatusesWithDescription = createSelector(
  getIntlSelector,
  getIsGroupedSelector,
  getTranlsatedIsGroup,
  (intl, isGrouped, translatedIsGroup) => {
    const statuses = translatedIsGroup.map(status => ({
      ...status,
      checked: isGrouped.includes(status.key),
    }));

    const description = isGrouped.length === 0 || isGrouped.length === IS_GROUPED_APPOINTMENT.length
      ? intl.formatMessage({ id: 'General.All', defaultMessage: '** All' })
      : statuses.filter(({ checked }) => checked).map(({ label }) => label).join(', ');

    return {
      statuses,
      description,
    };
  },
);

export const getServicesWithDescription = createSelector(
  getIntlSelector,
  getServicesSelector,
  getCheckedServicesSelector,
  (intl, _services, checkedServices = []) => {
    const services = _services.map(service => ({
      ...service,
      checked: checkedServices.includes(service.serviceId),
    }));

    const description = checkedServices.length === 0 || checkedServices.length === services.length
      ? intl.formatMessage({ id: 'General.All', defaultMessage: '** All' })
      : services.filter(({ checked }) => checked).map(({ name }) => name).join(', ');

    return {
      services,
      description,
    };
  },
);

export const getDateRangeDescription = createSelector(
  getIntlSelector,
  getDateRangeSelector,
  (intl, { startDate, endDate }) => {
    if (!startDate && !endDate) {
      return intl.formatMessage({ id: 'General.Any', defaultMessage: '** Any' });
    }

    const formattedStartDate = moment(startDate).format('DD/MM/YYYY');
    const formattedEndDate = moment(endDate).format('DD/MM/YYYY');

    if (startDate && !endDate) {
      const from = intl.formatMessage({
        id: 'Appointments.AppointmentsList.Filter.Schedule.From',
        defaultMessage: '** From',
      });

      return `${ from } ${ formattedStartDate }`;
    } else if (!startDate && endDate) {
      const to = intl.formatMessage({
        id: 'Appointments.AppointmentsList.Filter.Schedule.to',
        defaultMessage: '** To',
      });

      return `${ to } ${ formattedEndDate }`;
    }

    return `${ formattedStartDate } - ${ formattedEndDate }`;
  },
);

export const getActiveSortField = createSelector(
  getActiveSortFieldSelector,
  activeSortField => {
    if (!activeSortField) {
      return null;
    }

    const { label } = SORT_FIELDS.find(field => field.key === activeSortField.field);

    return {
      ...activeSortField,
      label,
    };
  },
);

export const getActiveFiltersAmount = createSelector(
  getLastConversationStatusesSelector,
  getCloseReasonsSelector,
  getDateRangeSelector,
  getServicesSelector,
  getCheckedServicesSelector,
  getScheduleTypesSelector,
  getIsGroupedSelector,
  getConversationTypesSelector,
  (conversationStatuses, closeReasons, daterange, services, checkedServices, scheduleTypes, isGrouped, conversationTypes) => {
    let activeFiltersAmount = 0;

    if (conversationStatuses.length > 0 && conversationStatuses.length !== CONVERSATION_STATUSES.length) {
      activeFiltersAmount += 1;
    }

    if (closeReasons.length > 0 && closeReasons.length !== CLOSE_REASONS.length) {
      activeFiltersAmount += 1;
    }

    if (scheduleTypes.length > 0 && scheduleTypes.length !== SCHEDULES.length) {
      activeFiltersAmount += 1;
    }

    if (daterange.startDate || daterange.endDate) {
      activeFiltersAmount += 1;
    }

    if (checkedServices.length > 0 && checkedServices.length !== services.length) {
      activeFiltersAmount += 1;
    }

    if (isGrouped.length > 0 && isGrouped.length !== IS_GROUPED_APPOINTMENT.length) {
      activeFiltersAmount += 1;
    }

    if (conversationTypes.length > 0 && conversationTypes.length !== CONVERSATION_TYPES.length) {
      activeFiltersAmount += 1;
    }

    return activeFiltersAmount;
  },
);

export const hasFiltersEnabled = createSelector(
  [getActiveFiltersAmount, getTextSearchFilter],
  (filtersAmount, textSearchFilter) => {
    return filtersAmount > 0 || !!textSearchFilter;
  },
);
